import {
    faChevronLeft,
    faChevronRight,
    faSpinner,
    faTimes,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { History, Location } from "history";
import React, { useEffect } from "react";

import { slugToParams } from "@/mk2/apps/bazaar/helpers";
import { ProductEntity } from "@/mk2/apps/bazaar/schemas";
import { bazaarProductDetailUrl } from "@/mk2/apps/bazaar/urls";
import { homepageUrl } from "@/mk2/apps/home/urls";
import { Img } from "@/mk2/components/Img";
import { Link } from "@/mk2/components/Link";
import { Panel } from "@/mk2/components/Panel";
import { url } from "@/mk2/helpers/urls";
import { interpolate } from "@/mk2/services/i18n";
import {
    BZ2_ITEM_CLOSE,
    BZ2_idx_FROM_total_LISTINGS,
    SERVER_FULL_NAME,
    SERVER_SLOGAN,
} from "@/mk/autogenerated/translation";
import { formatThousands } from "@/mk/bazaar/common/formatters";
import assetUrl from "@/mk/common/assetUrl";

import styles from "./Navigation.mscss";

export interface OwnProps {
    className?: string;
    history: History;
    back: Location;
    hits: number;
    index: number;
    nextProduct?: ProductEntity;
    previousProduct?: ProductEntity;
}

type Props = OwnProps;

const Navigation: React.FunctionComponent<Props> = ({
    className,
    history,
    back,
    hits,
    index,
    nextProduct,
    previousProduct,
}) => {
    const backTo = {
        ...back,
        state: {
            dontScrollOnPush: true,
            fromProductDetail: true,
        },
    };

    // Key handler
    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            // Ignore inputs
            if (
                ["input", "textarea", "select"].indexOf(
                    (event.target as HTMLElement).tagName.toLowerCase(),
                ) !== -1
            ) {
                return;
            }

            if (event.metaKey || event.altKey || event.ctrlKey) {
                return;
            }

            if (event.keyCode === 27) {
                // ESC
                history.push(backTo);
            } else if (event.keyCode === 37 && previousProduct) {
                // Arrow Left
                history.push(url(bazaarProductDetailUrl, slugToParams(previousProduct.slug)));
            } else if (event.keyCode === 39 && nextProduct) {
                // Arrow Right
                history.push(url(bazaarProductDetailUrl, slugToParams(nextProduct.slug)));
            }
        };

        window.addEventListener("keyup", handler);

        return () => {
            window.removeEventListener("keyup", handler);
        };
    }, [history, nextProduct, previousProduct]);

    return (
        <Panel className={cx(styles.Navigation, className)}>
            <Link className={styles.Navigation__close} to={backTo}>
                <span className={styles.Navigation__close__icon}>
                    <FontAwesomeIcon icon={faTimes} />
                </span>
                <span className={styles.Navigation__close__label}>{BZ2_ITEM_CLOSE}</span>
                <span className={styles.Navigation__close__shortcut}>(Esc)</span>
            </Link>
            <Link className={styles.Navigation__homepage} to={homepageUrl}>
                <Img
                    width={29}
                    height={23}
                    alt={SERVER_FULL_NAME + " - " + SERVER_SLOGAN}
                    src={assetUrl + "img/logos/ver-4/horse@2x.png"}
                    lazy={false}
                />
            </Link>
            <div className={styles.Navigation__navigation}>
                {previousProduct ? (
                    <Link
                        className={styles.Navigation__navigation__previous}
                        to={url(bazaarProductDetailUrl, slugToParams(previousProduct.slug))}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </Link>
                ) : (
                    <span
                        className={cx(
                            styles.Navigation__navigation__previous,
                            styles["Navigation__navigation__previous--disabled"],
                        )}
                    >
                        {index > 0 ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                            <FontAwesomeIcon icon={faChevronLeft} />
                        )}
                    </span>
                )}
                <div className={styles.Navigation__navigation__position}>
                    <span className={styles.Navigation__navigation__position__long}>
                        {interpolate(BZ2_idx_FROM_total_LISTINGS, {
                            idx: formatThousands(index + 1),
                            total: formatThousands(hits),
                        })}
                    </span>
                    <span className={styles.Navigation__navigation__position__short}>
                        {formatThousands(index + 1)}
                    </span>
                </div>
                {nextProduct ? (
                    <Link
                        className={styles.Navigation__navigation__next}
                        to={url(bazaarProductDetailUrl, slugToParams(nextProduct.slug))}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </Link>
                ) : (
                    <span
                        className={cx(
                            styles.Navigation__navigation__next,
                            styles["Navigation__navigation__next--disabled"],
                        )}
                    >
                        {index + 1 < hits ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                            <FontAwesomeIcon icon={faChevronRight} />
                        )}
                    </span>
                )}
            </div>
        </Panel>
    );
};

Navigation.displayName = "Navigation";

export default React.memo<Props>(Navigation);
